import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Button,
    TextField,
    Checkbox,
    FormControlLabel,
    Divider,
    CircularProgress,
    Alert,
} from "@mui/material";
import { Refresh, PhotoCamera, ArrowBack } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import SignatureCanvas from "react-signature-canvas";
import dayjs from "dayjs";
import axios from "axios";

import deliveryReceiptService from "../../services/deliveryReceiptService";
import systemSettingsService from "../../services/systemSettingsService";

const renderTemplate = (html, data) =>
    (html || "").replace(/\{\{\s*([\w]+)\s*\}\}/g, (match, key) =>
        Object.prototype.hasOwnProperty.call(data, key) ? data[key] : "",
    );

// Same grouping as the server's renderReceiptHtml (deliveryReceiptController.js)
// — a receipt is customer-scoped and can carry items from several different
// shipments, so this shows which shipment each package came from instead of
// a flat list.
const buildPackageRowsByShipment = (items) => {
    const groups = new Map();
    for (const it of items || []) {
        const shipmentId = (it.shipment?._id || it.shipment)?.toString() || "unknown";
        if (!groups.has(shipmentId)) {
            groups.set(shipmentId, { trackingNumber: it.shipment?.trackingNumber || "Shipment", items: [] });
        }
        groups.get(shipmentId).items.push(it);
    }

    return [...groups.values()]
        .map(
            (group) => `
      <div style="margin-bottom:10px">
        <div style="font-weight:700;font-size:12px;padding:6px 12px;background:#f5f5f5">Shipment ${group.trackingNumber}</div>
        ${group.items
            .map(
                (it, idx) => `
        <div style="display:flex;justify-content:space-between;padding:12px;${idx < group.items.length - 1 ? "border-bottom:1px solid #eee" : ""}">
          <div><div style="font-weight:600">${it.trackingNumber}</div><div style="font-size:11px;color:#666">${it.description || "Package"}</div></div>
        </div>`,
            )
            .join("")}
      </div>`,
        )
        .join("");
};

const RiderDeliveryConfirm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [receipt, setReceipt] = useState(null);
    const [templateHtml, setTemplateHtml] = useState("");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [receivedInGoodCondition, setReceivedInGoodCondition] = useState(true);
    const [damageNotes, setDamageNotes] = useState("");
    const [receivedByName, setReceivedByName] = useState("");
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [signatureEmpty, setSignatureEmpty] = useState(true);
    const [signatureDataUrl, setSignatureDataUrl] = useState(null);

    const sigRef = useRef(null);

    useEffect(() => {
        Promise.all([
            deliveryReceiptService.getOne(id),
            systemSettingsService.getDeliveryReceiptRiderTemplate(),
        ])
            .then(([receiptRes, templateRes]) => {
                const r = receiptRes.data.data.receipt;
                setReceipt(r);
                setReceivedByName(`${r.customer?.firstName || ""} ${r.customer?.lastName || ""}`.trim());
                setTemplateHtml(templateRes.data.data.templateHtml || "");
            })
            .catch(() => enqueueSnackbar("Failed to load delivery", { variant: "error" }))
            .finally(() => setLoading(false));
    }, [id]);

    const handlePhotoChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setPhotoFile(file);
        setPhotoPreview(URL.createObjectURL(file));
    };

    const uploadAsset = async (type, blob, contentType) => {
        const { data } = await deliveryReceiptService.presignAsset(id, type);
        const { key, url } = data.data;
        await axios.put(url, blob, { headers: { "Content-Type": contentType } });
        return key;
    };

    const handleSubmit = async () => {
        if (sigRef.current?.isEmpty()) {
            enqueueSnackbar("Please capture a signature", { variant: "warning" });
            return;
        }
        if (!receivedByName.trim()) {
            enqueueSnackbar("Recipient name is required", { variant: "warning" });
            return;
        }
        if (!receivedInGoodCondition && !damageNotes.trim()) {
            enqueueSnackbar("Please describe the damage or issue", { variant: "warning" });
            return;
        }

        setSubmitting(true);
        try {
            const canvas = sigRef.current.getCanvas();
            const dataUrl = canvas.toDataURL("image/png");
            const signatureBlob = await (await fetch(dataUrl)).blob();
            const signatureImageKey = await uploadAsset("signature", signatureBlob, "image/png");

            let deliveryPhotoKey;
            if (photoFile) {
                deliveryPhotoKey = await uploadAsset("photo", photoFile, photoFile.type);
            }

            await deliveryReceiptService.submit(id, {
                receivedInGoodCondition,
                damageNotes,
                receivedByName,
                signatureImageKey,
                deliveryPhotoKey,
            });

            enqueueSnackbar("Delivery confirmed", { variant: "success" });
            navigate("/rider/mine");
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to submit delivery", { variant: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    const renderedHtml = useMemo(() => {
        if (!receipt || !templateHtml) return "";

        const packageRows = buildPackageRowsByShipment(receipt.items);

        const data = {
            deliveryDate: dayjs().format("M-D-YYYY"),
            receiptNumber: receipt.receiptNumber,
            customerName: `${receipt.customer?.firstName || ""} ${receipt.customer?.lastName || ""}`.trim(),
            deliveredBy: "Waypoint Imports",
            customerPhone: receipt.customer?.phone || "—",
            deliveryAddress: receipt.deliveryAddress || "—",
            packageRows,
            packageCount: receipt.items?.length || 0,
            logoUrl: "/logos/waypoint-logo.png",
        };

        return renderTemplate(templateHtml, data);
    }, [receipt, templateHtml]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }
    if (!receipt) return null;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Button startIcon={<ArrowBack />} size="small" onClick={() => navigate("/rider/mine")} sx={{ alignSelf: "flex-start" }}>
                Back
            </Button>

            {/* One continuous card: static template on top, interactive fields
          seamlessly below — same background, no visual seam between them.
          Always white/dark-text regardless of app theme — this is a
          printed-document-style confirmation (the admin's HTML template
          assumes a white backdrop of its own), not a themed app surface. */}
            <Card sx={{ overflow: "hidden", bgcolor: "#fff", color: "#0E1420" }}>
                {/* Admin's HTML template — header/details/package info only.
            The interactive parts below (confirmation, acknowledgment,
            signature) are real React controls, styled to match. */}
                <Box dangerouslySetInnerHTML={{ __html: renderedHtml }} />

                {/* Interactive section — visually continues the same white
            card, same horizontal padding, no border/gap from the
            template above */}
                <Box sx={{ p: 3, pt: 0 }}>
                    <Divider sx={{ mb: 2.5 }} />

                    <Typography
                        variant="overline"
                        color="text.secondary"
                        display="block"
                        sx={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.05em", mb: 1 }}
                    >
                        Delivery Confirmation
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: 12.5 }}>
                        I confirm that I received the package(s) listed above. The package(s) were received in
                        acceptable condition unless noted below.
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
                        <FormControlLabel
                            sx={{ mx: 0 }}
                            control={
                                <Checkbox
                                    checked={receivedInGoodCondition}
                                    onChange={(e) => setReceivedInGoodCondition(e.target.checked)}
                                />
                            }
                            label="Received in good condition"
                        />
                        <FormControlLabel
                            sx={{ mx: 0 }}
                            control={
                                <Checkbox
                                    checked={!receivedInGoodCondition}
                                    onChange={(e) => setReceivedInGoodCondition(!e.target.checked)}
                                />
                            }
                            label="Damage or issue noted below"
                        />
                    </Box>

                    {!receivedInGoodCondition && (
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Notes / Damage / Missing Items *"
                            value={damageNotes}
                            onChange={(e) => setDamageNotes(e.target.value)}
                            sx={{ mb: 2.5 }}
                        />
                    )}

                    <Divider sx={{ mb: 2.5 }} />

                    <Typography
                        variant="overline"
                        color="text.secondary"
                        display="block"
                        sx={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.05em", mb: 1.5 }}
                    >
                        Customer Acknowledgment
                    </Typography>

                    <TextField
                        fullWidth
                        label="Received By (recipient name) *"
                        value={receivedByName}
                        onChange={(e) => setReceivedByName(e.target.value)}
                        helperText="If someone other than the customer is accepting the delivery, enter their name here."
                        sx={{ mb: 2.5 }}
                    />

                    <Typography variant="body2" fontWeight={600} mb={1}>
                        Delivery Photo (optional)
                    </Typography>
                    <Button component="label" variant="outlined" startIcon={<PhotoCamera />} fullWidth sx={{ mb: 2 }}>
                        {photoFile ? "Retake Photo" : "Take Photo"}
                        <input type="file" accept="image/*" capture="environment" hidden onChange={handlePhotoChange} />
                    </Button>
                    {photoPreview && (
                        <Box
                            component="img"
                            src={photoPreview}
                            sx={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 2, mb: 2.5 }}
                        />
                    )}

                    <Typography variant="body2" fontWeight={600} mb={1}>
                        Customer Signature *
                    </Typography>
                    <Box
                        sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            bgcolor: "action.hover",
                            mb: 1,
                            touchAction: "none",
                        }}
                    >
                        <SignatureCanvas
                            ref={sigRef}
                            penColor="#0D629B"
                            canvasProps={{ width: 560, height: 180, style: { width: "100%", height: "180px" } }}
                            onEnd={() => {
                                const empty = sigRef.current?.isEmpty() ?? true;
                                setSignatureEmpty(empty);
                                setSignatureDataUrl(empty ? null : sigRef.current.getCanvas().toDataURL("image/png"));
                            }}
                        />
                    </Box>
                    <Button
                        size="small"
                        startIcon={<Refresh />}
                        onClick={() => {
                            sigRef.current?.clear();
                            setSignatureEmpty(true);
                            setSignatureDataUrl(null);
                        }}
                        sx={{ mb: 1 }}
                    >
                        Clear Signature
                    </Button>

                    {signatureEmpty && (
                        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                            Ask the recipient to sign above before confirming.
                        </Alert>
                    )}

                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? <CircularProgress size={20} color="inherit" /> : "Confirm Delivery"}
                    </Button>
                </Box>
            </Card>
        </Box>
    );
};

export default RiderDeliveryConfirm;